<template>
  <div class="chat-sidebar" :class="{ open: open }" role="complementary" aria-label="Conversations">
    <!-- 1. Sidebar header -->
    <div class="sidebar-header">
      <span class="sidebar-title">Conversations</span>
      <span class="icon-btn close-btn" @click="$emit('close')" title="Close conversations" aria-label="Close conversations">
        <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" stroke-width="2" fill="none">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </span>
    </div>

    <!-- 2. + New Chat sticky button -->
    <button
      type="button"
      class="new-chat-btn"
      :disabled="isGenerating"
      @click="handleNewChat"
      aria-label="Start a new chat">
      + New Chat
    </button>

    <!-- 3. List area: empty state OR DxList -->
    <div class="sidebar-list-area" :class="{ locked: isGenerating }">
      <!-- Empty state (UI-04) -->
      <div v-if="chatList.length === 0" class="empty-state">
        <svg class="empty-icon" viewBox="0 0 24 24" width="48" height="48" stroke="#c8c6c4" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
        <div class="empty-title">No conversations yet</div>
        <div class="empty-body">Start chatting to create your first one.</div>
      </div>

      <!-- Conversation list -->
      <dx-list
        v-else
        :data-source="chatList"
        :selected-item-keys="selectedChatIdArray"
        selection-mode="single"
        :show-selection-controls="false"
        key-expr="id"
        no-data-text=""
        @item-click="onItemClick"
      >
        <template #item="{ data }">
          <div
            class="chat-row"
            :class="{ active: data.id === activeChatId }"
            :title="isGenerating ? 'Chat switching is disabled while AI is generating' : ''"
          >
            <div class="chat-row-main">
              <div v-if="renamingId !== data.id" class="chat-name" @dblclick.stop="startRename(data)">
                {{ data.name }}
              </div>
              <input
                v-else
                class="rename-input"
                type="text"
                v-model="renameText"
                placeholder="Chat name"
                aria-label="Rename chat"
                ref="renameInput"
                @click.stop
                @keydown.enter.stop.prevent="commitRename(data)"
                @keydown.esc.stop.prevent="cancelRename"
                @blur="commitRename(data)"
              />
              <div class="chat-timestamp">{{ formatTimestamp(data.changedOn) }}</div>
            </div>
            <div class="row-actions" @click.stop>
              <span
                class="icon-btn action-btn"
                @click="startRename(data)"
                :title="isGenerating ? 'Disabled while AI is generating' : 'Rename'"
                aria-label="Rename chat"
                :aria-disabled="isGenerating"
              >
                <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>
              </span>
              <span
                class="icon-btn action-btn delete-btn"
                @click="confirmDelete(data)"
                :title="isGenerating ? 'Disabled while AI is generating' : 'Delete'"
                aria-label="Delete chat"
                :aria-disabled="isGenerating"
              >
                <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>
              </span>
            </div>
          </div>
        </template>
      </dx-list>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { DxList } from 'devextreme-vue/list';
import { confirm } from 'devextreme/ui/dialog';
import formatRelative from '../../utils/formatRelative.js';

export default {
  name: 'ChatSidebar',
  components: { DxList },
  props: {
    open: { type: Boolean, default: false }
  },
  emits: ['close', 'update:open'],
  data() {
    return {
      renamingId: null,
      renameText: '',
      tsInterval: null
    };
  },
  computed: {
    ...mapState('chatStore', ['chatList', 'activeChatId', 'isGenerating']),
    selectedChatIdArray() {
      return this.activeChatId != null ? [this.activeChatId] : [];
    }
  },
  mounted() {
    this.tsInterval = setInterval(() => { this.$forceUpdate(); }, 60000);
  },
  beforeUnmount() {
    if (this.tsInterval) clearInterval(this.tsInterval);
  },
  methods: {
    ...mapActions('chatStore', ['switchChat', 'createChatPlaceholder', 'renameChat', 'deleteChat']),
    formatTimestamp(d) { return formatRelative(d); },

    onItemClick(e) {
      if (this.isGenerating) return;
      const chat = e.itemData;
      if (!chat || chat.id === this.activeChatId) {
        this.$emit('close');
        return;
      }
      this.switchChat(chat.id);
      this.$emit('close');
    },

    startRename(chat) {
      if (this.isGenerating) return;
      this.renamingId = chat.id;
      this.renameText = chat.name;
      this.$nextTick(() => {
        const input = this.$el && this.$el.querySelector('.rename-input');
        if (input) { input.focus(); input.select(); }
      });
    },
    commitRename(chat) {
      if (this.renamingId !== chat.id) return;
      const trimmed = (this.renameText || '').trim();
      if (!trimmed) {
        this.cancelRename();
        return;
      }
      if (trimmed !== chat.name) {
        this.renameChat({ id: chat.id, name: trimmed });
      }
      this.renamingId = null;
      this.renameText = '';
    },
    cancelRename() {
      this.renamingId = null;
      this.renameText = '';
    },

    async confirmDelete(chat) {
      if (this.isGenerating) return;
      const confirmed = await confirm(
        `Delete "${chat.name}"? This cannot be undone.`,
        'Delete conversation'
      );
      if (confirmed) {
        this.deleteChat(chat.id);
      }
    },

    handleNewChat() {
      if (this.isGenerating) return;
      this.createChatPlaceholder();
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
.chat-sidebar {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  z-index: 5;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  transition: left 0.25s ease;
  overflow: hidden;
}
.chat-sidebar.open { left: 0; }

.sidebar-header {
  height: 48px;
  padding: 0 16px;
  background: var(--tile-bg-color);
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}
.sidebar-title { font-size: 14px; font-weight: 600; }
.sidebar-header .icon-btn { cursor: pointer; opacity: 0.85; display: flex; align-items: center; }
.sidebar-header .icon-btn:hover { opacity: 1; }

.new-chat-btn {
  height: 44px;
  width: 100%;
  background: var(--tile-bg-color);
  color: white;
  border: none;
  border-radius: 0;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
}
.new-chat-btn:disabled,
.new-chat-btn[disabled] { opacity: 0.4; cursor: not-allowed; }

.sidebar-list-area {
  flex: 1;
  overflow-y: auto;
  background: #f3f2f1;
  position: relative;
}
.sidebar-list-area.locked { pointer-events: none; opacity: 0.4; }

.chat-row {
  position: relative;
  min-height: 48px;
  padding: 8px 16px 8px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  box-sizing: border-box;
}
.chat-row.active {
  border-left: 4px solid var(--tile-bg-color);
  padding-left: 12px;
  background: rgba(0,0,0,0.04);
}
.chat-row-main { flex: 1; min-width: 0; }
.chat-name {
  font-size: 14px;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rename-input {
  width: 100%;
  font-size: 14px;
  font-family: inherit;
  border: none;
  border-bottom: 1px solid var(--tile-bg-color);
  outline: none;
  padding: 2px 0;
  background: transparent;
}
.chat-timestamp { font-size: 12px; color: #605e5c; line-height: 1; margin-top: 4px; }

.row-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.15s ease;
}
.chat-row:hover .row-actions,
.chat-row:focus-within .row-actions,
.chat-row.active .row-actions { opacity: 1; }

.row-actions .action-btn {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  color: #605e5c;
}
.row-actions .action-btn:hover { background: rgba(0,0,0,0.06); color: #201f1e; }
.row-actions .action-btn.delete-btn:hover { color: #c50f1f; }
.row-actions .action-btn[aria-disabled="true"] {
  cursor: not-allowed;
  opacity: 0.35;
  pointer-events: none;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 24px 16px;
  text-align: center;
}
.empty-icon { margin-bottom: 16px; }
.empty-title { font-size: 14px; font-weight: 600; color: #605e5c; margin-bottom: 4px; }
.empty-body { font-size: 13px; font-weight: 400; color: #a19f9d; }
</style>
